import type { BuildingInfo } from '@/shared/types'

import { useFirestoreDocument } from './useFirestoreDocument'

interface UseBuildingInfoResult {
  buildingInfo: BuildingInfo | null
  loading: boolean
  error: Error | null
}

/**
 * Hook to fetch building information from Firestore for a specific building UUID
 *
 * @param buildingUuid - The UUID of the building. If undefined, no fetch is performed.
 * @returns Object containing:
 *   - buildingInfo: The building data (null if not found or buildingUuid is undefined)
 *   - loading: True while fetching data
 *   - error: Error object if fetch failed, null otherwise
 *
 * @example
 * const { buildingInfo, loading, error } = useBuildingInfo('building-123')
 */
export function useBuildingInfo(buildingUuid: string | undefined): UseBuildingInfoResult {
  const path = buildingUuid ? `/building_information/${buildingUuid}` : ''

  const { data, loading, error } = useFirestoreDocument<BuildingInfo>(path)

  return {
    buildingInfo: data,
    loading,
    error,
  }
}
